/**
 * reCAPTCHA v3 token helper for the public signup and send-OTP calls.
 *
 * Loads Google's script on first use (only pages that need a token pay for
 * it), executes the given action and returns the token. It never throws and
 * never blocks the caller: on any failure — script blocked by an extension,
 * offline, slow network — it resolves to `null`, and the server treats a
 * missing token as "let it pass". A present token is scored server-side.
 */
import { RECAPTCHA_SITE_KEY } from "./constants";

const SCRIPT_ID = "nbyula-recaptcha-v3";
const LOAD_TIMEOUT_MS = 5000;
const EXECUTE_TIMEOUT_MS = 5000;

let loadPromise = null;

/** Pure-ish: the token header the server also accepts on GET requests. */
export const RECAPTCHA_HEADER = "x-recaptcha-token";

function withTimeout(promise, ms) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      }
    );
  });
}

function loadScript() {
  if (typeof window === "undefined" || !RECAPTCHA_SITE_KEY) {
    return Promise.resolve(null);
  }
  if (window.grecaptcha?.execute) return Promise.resolve(window.grecaptcha);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement("script");
    const done = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
      } else {
        resolve(null);
      }
    };
    if (!existing) {
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
        RECAPTCHA_SITE_KEY
      )}`;
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    }
    script.addEventListener("load", done);
    if (existing && window.grecaptcha) done();
  });
  return loadPromise;
}

/**
 * Get a reCAPTCHA v3 token for `action`, or `null` if one cannot be obtained
 * within a few seconds. Safe to call on every attempt: tokens are single-use.
 */
export async function getRecaptchaToken(action) {
  try {
    const grecaptcha = await withTimeout(loadScript(), LOAD_TIMEOUT_MS);
    if (!grecaptcha?.execute) return null;
    const token = await withTimeout(
      grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }),
      EXECUTE_TIMEOUT_MS
    );
    return typeof token === "string" && token ? token : null;
  } catch (e) {
    return null;
  }
}

/** Axios config carrying the token as a header; `{}` when there is none. */
export async function recaptchaHeaders(action) {
  const token = await getRecaptchaToken(action);
  return token ? { headers: { [RECAPTCHA_HEADER]: token } } : {};
}
